import React, { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  query,
  orderBy,
  Timestamp,
  doc,
  updateDoc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

export default function CreditAdmin() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('logs');
  const [filter, setFilter] = useState('');

  const [uid, setUid] = useState('');
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [creditType, setCreditType] = useState('free');
  const [adminId, setAdminId] = useState(null);
  const [freeCredits, setFreeCredits] = useState(null);
  const [paidCredits, setPaidCredits] = useState(null);

  const navigate = useNavigate();

  // Detect current admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setAdminId(user.uid);
    });
    return () => unsubscribe();
  }, []);

  // Fetch logs live
  useEffect(() => {
    const q = query(collection(db, 'creditLogs'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log('📥 creditLogs snapshot received:', snapshot.size);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLogs(data);
        setLoading(false);
      },
      (error) => {
        console.error('❌ Firestore snapshot error:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Fetch user credit balance
  useEffect(() => {
    if (!uid) {
      setFreeCredits(null);
      setPaidCredits(null);
      return;
    }

    const fetchCredits = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          const data = snap.data();
          setFreeCredits(data.freeListingCredits ?? 0);
          setPaidCredits(data.paidListingCredits ?? 0);
          console.log('🎯 Fetched credit balances:', data);
        } else {
          setFreeCredits(null);
          setPaidCredits(null);
          console.warn('⚠️ No user found for UID:', uid);
        }
      } catch (err) {
        console.error('❌ Failed to fetch credits:', err);
        setFreeCredits(null);
        setPaidCredits(null);
      }
    };

    fetchCredits();
  }, [uid]);

  const filteredLogs = logs.filter((log) =>
    (log.type || '').toLowerCase().includes(filter.toLowerCase()) ||
    (log.uid || '').toLowerCase().includes(filter.toLowerCase()) ||
    (log.adminId || '').toLowerCase().includes(filter.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uid || !amount || !adminId) return alert('❌ Missing fields');

    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return alert('❌ User not found');

      const field = creditType === 'free' ? 'freeListingCredits' : 'paidListingCredits';
      const current = userSnap.data()[field] ?? 0;
      const updated = Math.max(0, current + amount);

      await updateDoc(userRef, {
        [field]: updated,
      });

      await addDoc(collection(db, 'creditLogs'), {
        uid,
        adminId,
        amount,
        creditType,
        reason: reason || '',
        type: amount > 0 ? 'manual_add' : 'manual_deduct',
        timestamp: Timestamp.now(),
      });

      alert(`✅ ${creditType.toUpperCase()} credits updated.`);
      setUid('');
      setAmount(0);
      setReason('');
      setFreeCredits(null);
      setPaidCredits(null);
    } catch (err) {
      console.error('❌ Credit update error:', err);
      alert('Failed to update credits');
    }
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>← Back</button>
      <h2 style={styles.header}>💳 Credit Admin Panel</h2>

      <div style={styles.tabButtons}>
        <button onClick={() => setTab('logs')} style={tab === 'logs' ? styles.activeTab : styles.tab}>View Logs</button>
        <button onClick={() => setTab('manual')} style={tab === 'manual' ? styles.activeTab : styles.tab}>Manual Add/Deduct</button>
      </div>

      {tab === 'manual' && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3>✍️ Adjust Credits</h3>
          <input
            placeholder="User UID"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            style={styles.input}
          />
          {freeCredits !== null && paidCredits !== null && (
            <>
              <p style={styles.balance}>Free: {freeCredits} • Paid: {paidCredits} • Total: {freeCredits + paidCredits}</p>
            </>
          )}
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                value="free"
                checked={creditType === 'free'}
                onChange={() => setCreditType('free')}
              />
              Free Credits
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                value="paid"
                checked={creditType === 'paid'}
                onChange={() => setCreditType('paid')}
              />
              Paid Credits
            </label>
          </div>
          <input
            type="number"
            placeholder="Amount (+ or -)"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            style={styles.input}
          />
          <input
            placeholder="Reason (e.g. bonus, refund)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={styles.input}
          />
          <button type="submit" style={styles.submitButton}>Submit</button>
        </form>
      )}

      {tab === 'logs' && (
        <>
          <input
            type="text"
            placeholder="Search logs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.search}
          />
          {loading ? (
            <p>Loading logs...</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Credit</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Admin</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td style={styles.td}>{log.timestamp?.toDate?.().toLocaleString() || '—'}</td>
                    <td style={styles.td}>{log.uid}</td>
                    <td style={styles.td}>{log.amount}</td>
                    <td style={styles.td}>{log.type}</td>
                    <td style={styles.td}>{log.creditType}</td>
                    <td style={styles.td}>{log.reason || '—'}</td>
                    <td style={styles.td}>{log.adminId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, sans-serif',
    minHeight: '100vh',
  },
  backButton: {
    marginBottom: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#eee',
    cursor: 'pointer',
  },
  header: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#0f5132',
  },
  tabButtons: {
    marginBottom: '1rem',
  },
  tab: {
    marginRight: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#f0f0f0',
    cursor: 'pointer',
  },
  activeTab: {
    fontWeight: 'bold',
    backgroundColor: '#d1e7dd',
    marginRight: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #999',
    cursor: 'pointer',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '400px',
    marginBottom: '2rem',
  },
  input: {
    marginBottom: '0.75rem',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
  },
  radioGroup: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '0.75rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  submitButton: {
    padding: '0.5rem',
    borderRadius: '6px',
    backgroundColor: '#0f5132',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
  },
  balance: {
    marginBottom: '0.5rem',
    color: '#0c4128',
    fontWeight: '600',
  },
  search: {
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '100%',
    maxWidth: '300px',
    marginBottom: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  th: {
    borderBottom: '2px solid #ccc',
    padding: '0.75rem',
    backgroundColor: '#e6f2ff',
    textAlign: 'left',
  },
  td: {
    borderBottom: '1px solid #ddd',
    padding: '0.75rem',
  },
};
